const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

/* -------------------------------------------------------------------------
   Keep discount codes alive through express checkout. Added 2026-08-10.

   A code typed into the cart is stored on the cart object only. The
   accelerated checkout buttons (Apple Pay / Shop Pay / Google Pay) build
   their checkout straight from the cart and drop it, so the buyer watches
   the discount apply and then pays full price - see order #5567.

   Hitting the /discount/<code> route puts the code in the session instead,
   which every checkout path carries. Two things feed that session:
     1. codes typed into the cart, mirrored whenever the cart changes
     2. the signup popup, armed the moment it reveals the code
------------------------------------------------------------------------- */
(function () {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  let lastSynced = null;
  let armedCode = null;
  let pending = false;

  function applyToSession(code, redirect) {
    return fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${redirect || root + 'cart.js'}`, {
      credentials: 'same-origin',
    });
  }

  /* --- 1. cart codes ---------------------------------------------------- */

  async function syncDiscountSession() {
    if (pending) return;
    pending = true;

    try {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      const cart = await response.json();
      const codes = (cart.discount_codes || [])
        .filter((discount) => discount.applicable)
        .map((discount) => discount.code);
      const key = codes.join(',');

      if (key === lastSynced) return;

      if (codes.length === 0) {
        // Only clear a session code we put there ourselves, so that a visitor
        // arriving on a /discount/ link - or one armed by the popup - keeps it.
        if (lastSynced) {
          document.cookie = `discount_code=; Max-Age=0; path=${root}`;
          armedCode = null;
        }
        lastSynced = '';
        return;
      }

      for (const code of codes) {
        await applyToSession(code);
      }

      lastSynced = key;
    } catch (error) {
      // Never let this block the cart.
    } finally {
      pending = false;
    }
  }

  document.addEventListener('cart:updated', syncDiscountSession);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest('[data-discount-btn]')) {
      setTimeout(syncDiscountSession, 1200);
    }
  });

  /* --- 2. signup popup -------------------------------------------------- */

  function armPopupCode() {
    const done = document.getElementById('sb-step-done');
    if (!done || done.hasAttribute('hidden')) return;

    const codeElement = done.querySelector('.sb-code');
    const code = codeElement ? codeElement.textContent.trim() : '';
    if (!code || code === armedCode) return;

    armedCode = code;
    applyToSession(code).catch(() => {});
  }

  // Belt and braces: if they click through on the popup's own button, carry the
  // code in the URL as well, so it lands even if the fetch above was blocked.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target ? target.closest('#sb-step-done a[href]') : null;
      if (!link) return;

      const href = link.getAttribute('href') || `${root}collections/all`;
      if (href.indexOf('/discount/') !== -1) return;

      const codeElement = document.querySelector('#sb-step-done .sb-code');
      const code = codeElement ? codeElement.textContent.trim() : '';
      if (!code) return;

      event.preventDefault();
      window.location.href = `${root}discount/${encodeURIComponent(code)}?redirect=${href}`;
    },
    true
  );

  function watchPopup() {
    const done = document.getElementById('sb-step-done');
    if (!done) return false;

    new MutationObserver(armPopupCode).observe(done, {
      attributes: true,
      attributeFilter: ['hidden', 'class', 'style'],
    });
    armPopupCode();
    return true;
  }

  function start() {
    syncDiscountSession();

    if (watchPopup()) return;
    // The popup is injected late on some pages - retry briefly, then give up.
    let tries = 0;
    const timer = setInterval(() => {
      if (watchPopup() || ++tries > 20) clearInterval(timer);
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();